import React, { useState, useEffect } from 'react';
import { Box, Typography, InputBase, alpha, Drawer, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CodeIcon from '@mui/icons-material/Code';
import QuizIcon from '@mui/icons-material/Quiz';
import LockIcon from '@mui/icons-material/Lock';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export const WorkspaceSidebar = ({
  sidebarCollapsed,
  setSidebarCollapsed,
  sidebarWidth,
  sidebarSearch,
  setSidebarSearch,
  handleDragStart,
  activeCategoryData,
  topicId,
  problemId,
  isPurchased,
  setAlertMessage,
  setAlertOpen,
  categoryId,
  navigate,
  activeItemRef,
  isLight,
  basePath,
  previewIds
}) => {
  const [z2hProgress, setZ2hProgress] = useState(
    parseInt(localStorage.getItem('foundation_progress') || '1', 10)
  );
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    const handleProgressUpdate = () => {
      setZ2hProgress(parseInt(localStorage.getItem('foundation_progress') || '1', 10));
    };
    window.addEventListener('foundation_progress_updated', handleProgressUpdate);
    return () => window.removeEventListener('foundation_progress_updated', handleProgressUpdate);
  }, []);

  return (
    <>
      {/* LEFT SIDEBAR: Full Category Curriculum */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        width: sidebarCollapsed ? 0 : `${sidebarWidth}px`,
        minWidth: sidebarCollapsed ? 0 : `${sidebarWidth}px`,
        maxWidth: sidebarCollapsed ? 0 : `${sidebarWidth}px`,
        borderRight: '1px solid var(--border-main)',
        bgcolor: 'var(--bg-sidebar)',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
        transition: sidebarCollapsed ? 'all 0.2s ease' : 'none',
        flexShrink: 0,
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'var(--scrollbar-thumb)', borderRadius: '4px' }
      }}>
        {/* Sidebar Header */}
        <Box sx={{ p: 2, borderBottom: '1px solid var(--border-main)', bgcolor: 'var(--bg-paper)', position: 'sticky', top: 0, zIndex: 5 }}>
          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.8px', textTransform: 'uppercase', fontSize: '0.62rem' }}>
            Curriculum Track
          </Typography>
          <Typography sx={{ fontWeight: 800, color: 'var(--text-primary)', mt: 0.25, lineHeight: 1.25, fontSize: '0.82rem' }}>
            {activeCategoryData.title}
          </Typography>

          {!sidebarCollapsed && (
            <Box sx={{
              mt: 1.5,
              bgcolor: isLight ? alpha('#000', 0.04) : alpha('#fff', 0.05),
              borderRadius: 2,
              px: 1,
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              border: '1px solid',
              borderColor: isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
              '&:focus-within': {
                borderColor: 'primary.main',
                bgcolor: 'transparent'
              }
            }}>
              <SearchIcon sx={{ fontSize: 16, color: 'var(--text-secondary)' }} />
              <InputBase
                placeholder="Search pattern or problem..."
                value={sidebarSearch}
                onChange={(e) => setSidebarSearch(e.target.value)}
                sx={{
                  fontSize: '0.74rem',
                  color: 'var(--text-primary)',
                  width: '100%',
                  '& input::placeholder': { fontSize: '0.74rem', opacity: 0.7 }
                }}
              />
            </Box>
          )}
        </Box>

        {/* Topics List */}
        <Box sx={{ p: 2 }}>
          {[...activeCategoryData.topics]
            .sort((a, b) => {
              if (a.group === 'Linear' && b.group !== 'Linear') return -1;
              if (a.group !== 'Linear' && b.group === 'Linear') return 1;
              return 0;
            })
            .map(topic => {
              const search = sidebarSearch.toLowerCase();
              if (!search) return { ...topic, matched: true, matchedProblems: topic.problems };

              const topicMatch = topic.title.toLowerCase().includes(search);
              const matchedProblems = topic.problems?.filter(p => p.title.toLowerCase().includes(search));
              const hasProblemMatch = matchedProblems && matchedProblems.length > 0;

              if (!topicMatch && !hasProblemMatch) return null;

              return {
                ...topic,
                matched: true,
                matchedProblems: hasProblemMatch ? matchedProblems : topic.problems
              };
            })
            .filter(Boolean)
            .map((topic, index, arr) => {
              const showHeader = topic.group && (index === 0 || (arr[index - 1] && topic.group !== arr[index - 1].group));
              const isTopicActive = topicId === topic.id && !problemId;

              return (
                <React.Fragment key={topic.id}>
                  {showHeader && (() => {
                    const phaseMatch = topic.group.match(/^Phase\s+(\d+)$/);
                    const phaseNum = phaseMatch ? parseInt(phaseMatch[1], 10) : 999;
                    const isCompleted = categoryId === 'foundation' && phaseNum < z2hProgress;

                    return (
                      <Box sx={{ mb: 1.5, mt: index === 0 ? 0 : 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border-main)', pb: 0.75 }}>
                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 800, letterSpacing: '0.8px', fontSize: '0.62rem', textTransform: 'uppercase' }}>
                          {topic.group}
                        </Typography>
                        {isCompleted && (
                          <Box sx={{ bgcolor: 'rgba(16, 185, 129, 0.15)', color: '#10b981', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.55rem', fontWeight: 800, textTransform: 'uppercase' }}>
                            Completed
                          </Box>
                        )}
                      </Box>
                    );
                  })()}
                  <Box sx={{ mb: 2 }}>
                    {(() => {
                      // Determine lock state for this topic
                      let isLocked = false;
                      let lockMessage = '🔒 Locked — Purchase to unlock full access.';
                      
                      if (categoryId === 'foundation') {
                        const phaseMatch = topic.group?.match(/^Phase\s+(\d+)$/);
                        const phaseNum = phaseMatch ? parseInt(phaseMatch[1], 10) : 999;
                        const isPhase1 = phaseNum === 1;
                        
                        if (isPhase1) {
                          isLocked = false;
                        } else if (!isPurchased) {
                          isLocked = true;
                          lockMessage = '🔒 Locked — Purchase to unlock full access.';
                        } else if (phaseNum > z2hProgress) {
                          isLocked = true;
                          lockMessage = '🔒 Complete the current phase first to unlock this one.';
                        }
                      } else {
                        isLocked = !isPurchased && !previewIds.includes(topic.id);
                      }

                      if (topic.fileUrl && (!topic.problems || topic.problems.length === 0 || topic.fileUrl !== topic.problems[0].fileUrl)) {
                        return (
                          <Box
                            ref={isTopicActive ? activeItemRef : null}
                            onClick={() => {
                              if (isLocked) {
                                setAlertMessage(lockMessage);
                                setAlertOpen(true);
                                return;
                              }
                              navigate(`${basePath}/${topic.id}`);
                            }}
                            sx={{
                              p: 0.75, px: 1.25, borderRadius: 1.5, mb: 0.5,
                              display: 'flex', alignItems: 'center', gap: 1,
                              cursor: isLocked ? 'not-allowed' : 'pointer',
                              opacity: isLocked ? 0.5 : 1,
                              bgcolor: isTopicActive ? 'primary.main' : 'transparent',
                              color: isTopicActive ? '#fff' : 'var(--text-primary)',
                              '&:hover': isLocked ? {} : { bgcolor: isTopicActive ? 'primary.main' : (isLight ? 'rgba(0,0,0,0.05)' : '#18181b'), color: isTopicActive ? '#fff' : (isLight ? '#0f172a' : '#e2e8f0') }
                            }}
                          >
                            <Typography sx={{ color: 'inherit', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.78rem' }}>
                              <span style={{ color: isTopicActive ? '#fff' : '#3b82f6', fontSize: '0.72rem', fontWeight: 800 }}>{index + 1}.</span> 
                              {isLocked ? <LockIcon sx={{ fontSize: 11, mr: 0.5 }} /> : (topic.badge === 'Quiz' ? <QuizIcon sx={{ fontSize: 11, mr: 0.5 }} /> : null)}
                              {topic.title}
                            </Typography>
                          </Box>
                        );
                      } else {
                        return (
                          <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 0.5, ml: 0.5, display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.78rem' }}>
                            <span style={{ color: '#3b82f6', fontSize: '0.72rem', fontWeight: 800 }}>{index + 1}.</span> 
                            {isLocked && <LockIcon sx={{ fontSize: 11, mr: 0.5, opacity: 0.6 }} />}
                            {topic.title}
                          </Typography>
                        );
                      }
                    })()}

                    <Box sx={{ ml: 1.5, borderLeft: (topic.matchedProblems && topic.matchedProblems.length > 0) ? `1px solid ${isLight ? '#e2e8f0' : '#1f1f22'}` : 'none' }}>
                      {topic.matchedProblems?.map((problem) => {
                        const isProblemActive = problemId === problem.id || (!problemId && topicId === topic.id && topic.fileUrl === problem.fileUrl);
                        
                        // Foundation phase-aware locking for problems
                        let isProblemLocked = false;
                        let problemLockMessage = '🔒 Locked — Purchase to unlock full access.';
                        
                        if (categoryId === 'foundation') {
                          const phaseMatch = topic.group?.match(/^Phase\s+(\d+)$/);
                          const phaseNum = phaseMatch ? parseInt(phaseMatch[1], 10) : 999;
                          const isPhase1 = phaseNum === 1;
                          
                          if (isPhase1) {
                            isProblemLocked = false;
                          } else if (!isPurchased) {
                            isProblemLocked = true;
                            problemLockMessage = '🔒 Locked — Purchase to unlock full access.';
                          } else if (phaseNum > z2hProgress) {
                            isProblemLocked = true;
                            problemLockMessage = '🔒 Complete the current phase first to unlock this one.';
                          }
                        } else {
                          isProblemLocked = !isPurchased && !previewIds.includes(problem.id);
                        }
                        
                        return (
                          <Box
                            key={problem.id}
                            ref={isProblemActive ? activeItemRef : null}
                            onClick={() => {
                              if (isProblemLocked) {
                                setAlertMessage(problemLockMessage);
                                setAlertOpen(true);
                                return;
                              }
                              navigate(`${basePath}/${problem.id}`);
                            }}
                            sx={{
                              p: 0.75, px: 1.25, borderRadius: '0 8px 8px 0', mb: 0.25,
                              cursor: isProblemLocked ? 'not-allowed' : 'pointer',
                              opacity: isProblemLocked ? 0.5 : 1,
                              borderLeft: isProblemActive ? '3px solid #3b82f6' : '3px solid transparent',
                              bgcolor: isProblemActive ? (isLight ? '#eff6ff' : '#1e293b') : 'transparent',
                              color: isProblemActive ? 'primary.main' : (isLight ? '#64748b' : '#71717a'),
                              transition: 'all 0.2s ease',
                              '&:hover': (!isProblemLocked) ? { 
                                bgcolor: isProblemActive ? (isLight ? '#eff6ff' : '#1e293b') : (isLight ? 'rgba(0,0,0,0.03)' : '#18181b'),
                                color: isLight ? '#0f172a' : '#e2e8f0' 
                              } : {}
                            }}
                          >
                            <Typography sx={{ fontWeight: isProblemActive ? 700 : 500, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.74rem' }}>
                              <span style={{ opacity: 0.6, display: 'flex' }}>
                                {isProblemLocked ? <LockIcon sx={{ fontSize: 13 }} /> : <CodeIcon sx={{ fontSize: 13 }} />}
                              </span>
                              {problem.title}
                              {problem.badge && (
                                <Box component="span" sx={{ 
                                  ml: 'auto', 
                                  fontSize: '0.6rem', 
                                  px: 0.5, py: 0.05, 
                                  borderRadius: '4px', 
                                  fontWeight: 900,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.2px',
                                  color: problem.badge === 'Easy' ? '#15803d' : problem.badge === 'Medium' ? '#d97706' : problem.badge === 'Hard' ? '#dc2626' : (isLight ? '#6366f1' : '#818cf8'),
                                  bgcolor: problem.badge === 'Easy' ? '#dcfce720' : problem.badge === 'Medium' ? '#fef3c720' : problem.badge === 'Hard' ? '#fef2f220' : '#6366f115',
                                  border: '1px solid currentColor',
                                  opacity: 0.9,
                                  flexShrink: 0
                                }}>
                                  {problem.badge}
                                </Box>
                              )}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </React.Fragment>
              );
            })}
        </Box>
      </Box>

      {/* DRAG HANDLE + COLLAPSE TOGGLE between sidebar and main */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, position: 'relative', width: '5px', flexShrink: 0, zIndex: 10, alignItems: 'center' }}>
        <Box
          onMouseDown={handleDragStart}
          sx={{
            position: 'absolute', top: 0, bottom: 0, left: 0, right: 0,
            cursor: 'col-resize',
            '&:hover': { bgcolor: 'primary.main', opacity: 0.8 },
            transition: 'background 0.15s',
          }}
        />
        <Box
          onClick={() => setSidebarCollapsed(c => !c)}
          sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 18, height: 36,
            bgcolor: 'var(--bg-paper)', border: '1px solid var(--border-main)', borderRadius: '4px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', zIndex: 20, color: 'var(--text-secondary)',
            '&:hover': { bgcolor: 'primary.main', color: '#fff', borderColor: 'primary.main' },
            transition: 'all 0.15s',
          }}
        >
          {sidebarCollapsed ? <ChevronRightIcon sx={{ fontSize: 14 }} /> : <ChevronLeftIcon sx={{ fontSize: 14 }} />}
        </Box>
      </Box>

      {/* MOBILE FLOATING CATALOG BUTTON */}
      <IconButton
        onClick={() => setMobileDrawerOpen(true)}
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed', bottom: 80, left: 16, zIndex: 1300,
          bgcolor: 'primary.main', color: '#fff',
          width: 44, height: 44,
          boxShadow: '0 4px 16px rgba(59,130,246,0.5)',
          '&:hover': { bgcolor: 'primary.dark' }
        }}
      >
        <MenuIcon sx={{ fontSize: 20 }} />
      </IconButton>

      {/* MOBILE DRAWER */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: '80vw', maxWidth: 300,
            bgcolor: 'var(--bg-sidebar)',
            borderRight: '1px solid var(--border-main)'
          }
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid var(--border-main)', bgcolor: 'var(--bg-paper)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.8px', textTransform: 'uppercase', fontSize: '0.62rem' }}>
              Curriculum Track
            </Typography>
            <Typography sx={{ fontWeight: 800, color: 'var(--text-primary)', mt: 0.25, lineHeight: 1.25, fontSize: '0.82rem' }}>
              {activeCategoryData.title}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setMobileDrawerOpen(false)} sx={{ color: 'var(--text-secondary)' }}>
            <ChevronLeftIcon />
          </IconButton>
        </Box>
        <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
          {[...activeCategoryData.topics]
            .sort((a, b) => {
              if (a.group === 'Linear' && b.group !== 'Linear') return -1;
              if (a.group !== 'Linear' && b.group === 'Linear') return 1;
              return 0;
            })
            .map((topic, index, arr) => {
              const showHeader = topic.group && (index === 0 || arr[index - 1]?.group !== topic.group);
              const isTopicActive = topicId === topic.id && !problemId;

              // Lock state for this topic
              let isLocked = false;
              if (categoryId === 'foundation') {
                const phaseMatch = topic.group?.match(/^Phase\s+(\d+)$/);
                const phaseNum = phaseMatch ? parseInt(phaseMatch[1], 10) : 999;
                if (phaseNum !== 1) {
                  isLocked = !isPurchased || phaseNum > z2hProgress;
                }
              } else {
                isLocked = !isPurchased && !previewIds.includes(topic.id);
              }

              return (
                <React.Fragment key={topic.id}>
                  {showHeader && (
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 800, letterSpacing: '0.8px', fontSize: '0.62rem', textTransform: 'uppercase', display: 'block', mt: index === 0 ? 0 : 2, mb: 0.75, borderBottom: '1px solid var(--border-main)', pb: 0.5 }}>
                      {topic.group}
                    </Typography>
                  )}

                  {/* Topic row — only show if it has its own fileUrl (not just a container for problems) */}
                  {topic.fileUrl && (!topic.problems || topic.problems.length === 0 || topic.fileUrl !== topic.problems[0]?.fileUrl) && (
                    <Box
                      onClick={() => {
                        if (isLocked) { return; }
                        navigate(`${basePath}/${topic.id}`);
                        setMobileDrawerOpen(false);
                      }}
                      sx={{
                        p: 0.75, px: 1.25, borderRadius: 1.5, mb: 0.5,
                        cursor: isLocked ? 'not-allowed' : 'pointer',
                        opacity: isLocked ? 0.5 : 1,
                        bgcolor: isTopicActive ? 'primary.main' : 'transparent',
                        color: isTopicActive ? '#fff' : 'var(--text-primary)',
                        display: 'flex', alignItems: 'center', gap: 1,
                        '&:hover': isLocked ? {} : { bgcolor: isTopicActive ? 'primary.main' : 'rgba(59,130,246,0.08)' }
                      }}
                    >
                      {isLocked ? <LockIcon sx={{ fontSize: 12 }} /> : topic.badge === 'Quiz' ? <QuizIcon sx={{ fontSize: 12 }} /> : <CodeIcon sx={{ fontSize: 12 }} />}
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: isTopicActive ? 800 : 600, color: 'inherit' }}>
                        {topic.title}
                      </Typography>
                    </Box>
                  )}

                  {/* Topic label (no direct fileUrl — just a group heading for its problems) */}
                  {(!topic.fileUrl || (topic.problems?.length > 0 && topic.fileUrl === topic.problems[0]?.fileUrl)) && (
                    <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 0.5, ml: 0.5, display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.78rem' }}>
                      <span style={{ color: '#3b82f6', fontSize: '0.72rem', fontWeight: 800 }}>{index + 1}.</span>
                      {isLocked && <LockIcon sx={{ fontSize: 11, opacity: 0.6 }} />}
                      {topic.title}
                    </Typography>
                  )}

                  {/* Problems / Subtopics */}
                  {topic.problems && topic.problems.length > 0 && (
                    <Box sx={{ ml: 1.5, borderLeft: `1px solid ${isLocked ? 'transparent' : '#3b82f620'}`, mb: 1 }}>
                      {topic.problems.map((problem) => {
                        const isProblemActive = problemId === problem.id || (!problemId && topicId === topic.id && topic.fileUrl === problem.fileUrl);

                        let isProblemLocked = false;
                        if (categoryId === 'foundation') {
                          const phaseMatch = topic.group?.match(/^Phase\s+(\d+)$/);
                          const phaseNum = phaseMatch ? parseInt(phaseMatch[1], 10) : 999;
                          if (phaseNum !== 1) {
                            isProblemLocked = !isPurchased || phaseNum > z2hProgress;
                          }
                        } else {
                          isProblemLocked = !isPurchased && !previewIds.includes(problem.id);
                        }

                        return (
                          <Box
                            key={problem.id}
                            onClick={() => {
                              if (isProblemLocked) { return; }
                              navigate(`${basePath}/${problem.id}`);
                              setMobileDrawerOpen(false);
                            }}
                            sx={{
                              p: 0.75, px: 1.25, borderRadius: '0 8px 8px 0', mb: 0.25,
                              cursor: isProblemLocked ? 'not-allowed' : 'pointer',
                              opacity: isProblemLocked ? 0.5 : 1,
                              borderLeft: isProblemActive ? '3px solid #3b82f6' : '3px solid transparent',
                              bgcolor: isProblemActive ? 'rgba(59,130,246,0.1)' : 'transparent',
                              color: isProblemActive ? '#3b82f6' : 'var(--text-secondary)',
                              transition: 'all 0.15s ease',
                              display: 'flex', alignItems: 'center', gap: 1,
                              '&:hover': isProblemLocked ? {} : {
                                bgcolor: isProblemActive ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.05)',
                                color: 'var(--text-primary)'
                              }
                            }}
                          >
                            <span style={{ opacity: 0.6, display: 'flex', flexShrink: 0 }}>
                              {isProblemLocked ? <LockIcon sx={{ fontSize: 12 }} /> : <CodeIcon sx={{ fontSize: 12 }} />}
                            </span>
                            <Typography sx={{ fontSize: '0.74rem', fontWeight: isProblemActive ? 700 : 500, color: 'inherit', flex: 1 }}>
                              {problem.title}
                            </Typography>
                            {problem.badge && (
                              <Box component="span" sx={{
                                fontSize: '0.58rem', px: 0.5, py: 0.1, borderRadius: '4px', fontWeight: 900,
                                textTransform: 'uppercase', letterSpacing: '0.2px', flexShrink: 0,
                                color: problem.badge === 'Easy' ? '#15803d' : problem.badge === 'Medium' ? '#d97706' : problem.badge === 'Hard' ? '#dc2626' : '#818cf8',
                                border: '1px solid currentColor', opacity: 0.85
                              }}>
                                {problem.badge}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
        </Box>
      </Drawer>
    </>
  );
};
